import React, { useEffect } from 'react';
import { Modal } from '../../elements/Modal';
import TableRow from './TableRow';
import Omega3EditorOption from './Omega3EditorOption';
import { useTranslation } from 'react-i18next';
import { isGermanLanguage } from '../../../utils/language';
import { Icon } from '../../elements/icon';
import {
  AddButton,
  Close,
  DoneBackToThePage,
  IncludesCarousel,
  Nutrient,
  NutrientsCarousel,
  Quantity,
  ResetButton,
} from '../../../Constants';

const EditFormulaModal = ({
  open,
  handleClose,
  data,
  ingredients,
  hasFormulaChanged,
  setAddIngredientsModal,
  allIngredients,
  onRemoveQuantity,
  onAddQuantity,
  prices,
  removeIngredientFromBin,
  onInputChange,
  onReset,
  name,
  onReview,
  isReviewDisabled = false,
  reviewMessage = null,
  renewalPriceMessage = null,
  isCustomFormula = false,
  showScientificName = true,
  omega3Option,
}) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      className="t-w-full t-max-w-[95vw] sm:t-max-w-[500px] t-h-[80vh] !t-px-0 t-overflow-hidden t-flex t-flex-col"
      childrenClass="!t-h-full !t-flex !t-flex-col !t-items-stretch !t-justify-start !t-gap-0 !t-overflow-hidden"
      notCrossIcon
      enableClickOutsideClose={false}
    >
      {/* Header */}
      <div className="t-flex-shrink-0 t-w-full t-flex t-items-center t-justify-between t-px-3 t-py-3 t-bg-[#103642] t-z-10">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}
          className="t-flex t-items-center t-cursor-pointer hover:t-opacity-70 t-bg-transparent t-border-0 t-p-0"
        >
          <Icon name="chevronLeft" size={24} color="#fff" raw />
          <span className="t-text-white t-text-sm">{t('EditFormula_BackToFormula')}</span>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }}
          className="t-flex t-items-center t-cursor-pointer hover:t-opacity-70 t-bg-transparent t-border-0 t-p-0"
        >
          <span className="t-text-white t-text-sm">{t(Close)}</span>
          <Icon name="cross" className="t-fill-white" size={24} raw />
        </button>
      </div>

      {/* Warning banner */}
      <div className="t-flex-shrink-0 t-bg-[#FFF8E1] t-border-b t-border-[#F6E147] t-px-4 t-py-3 t-flex t-gap-2 t-items-start">
        <span className="t-text-base t-flex-shrink-0">⚠️</span>
        <p className="!t-m-0 t-text-xs t-text-[#103642] t-leading-relaxed">
          {t('EditFormula_Warning')}
          {renewalPriceMessage && (
            <span aria-live="polite" className="t-block t-mt-2 t-font-semibold">
              {renewalPriceMessage}
            </span>
          )}
        </p>
      </div>

      <div className="t-flex-1 t-overflow-y-auto t-overflow-x-hidden hide-scrollbar t-min-h-0">
        <div className="t-flex t-flex-col t-gap-3 t-py-4">
          {/* Title row */}
          <div className="t-px-4 t-flex t-items-center t-justify-between t-gap-2">
            <div>
              <h1 className="!t-m-0 t-font-serif t-font-normal t-text-navy !t-text-[22px]">
                {name}
              </h1>
              <p className="!t-m-0 t-font-sans t-text-sm t-text-navy/70">
                {t(IncludesCarousel)} {data?.length} {t(NutrientsCarousel)}
              </p>
            </div>
            <div className="t-flex t-items-center t-gap-2">
              <button
                className="t-bg-transparent t-border t-border-navy/20 t-text-navy t-text-xs t-font-semibold t-rounded-lg t-px-3 t-py-1.5 t-cursor-pointer hover:t-bg-navy/5 t-transition-colors disabled:t-opacity-40"
                onClick={onReset}
                disabled={!hasFormulaChanged}
              >
                {t(ResetButton)}
              </button>
              <button
                className="t-bg-navy t-text-white t-border-0 t-text-xs t-font-semibold t-rounded-lg t-px-3 t-py-1.5 t-cursor-pointer hover:t-opacity-90 t-transition-opacity"
                onClick={() => setAddIngredientsModal(true)}
              >
                {t(AddButton)}
              </button>
            </div>
          </div>

          {omega3Option?.hasAdded && (
            <div className="t-px-4">
              <Omega3EditorOption {...omega3Option} />
            </div>
          )}

          {/* Column headers */}
          <div className="t-px-4 t-flex t-items-center t-justify-between t-gap-3">
            <div className="t-flex-1 t-text-[10px] t-font-bold t-text-navy/40 t-uppercase t-tracking-wider">
              {t(Nutrient)}
            </div>
            <div className="t-flex t-items-center t-gap-3 t-flex-shrink-0 t-text-[10px] t-font-bold t-text-navy/40 t-uppercase t-tracking-wider">
              <span className="t-w-[58px] t-text-right t-pr-5">{t(Quantity)}</span>
              <span className="t-min-w-[38px] t-text-right">{isGermanLanguage(i18n.language) ? t('OrderDetails_Col_NRV') : '%NRV'}</span>
              <span className="t-min-w-[36px] t-text-right">€</span>
              <span className="t-w-4" />
            </div>
          </div>

          {/* Ingredient rows */}
          <div className="t-px-4">
            {data &&
              data.map((item, i) => {
                const ingredient = allIngredients?.data?.find(
                  (ing) => ing.ingredient === item?.ingredient || ing.ingredient_de === item?.ingredient
                );
                const ingredientData = ingredient;
                const euNrv = parseFloat(ingredientData?.eu_nrv);
                const originalDose = parseFloat(
                  ingredients?.find((ing) => String(ing?.ingredient_id) === String(item?.ingredient_id))?.daily_dose
                ) || 0;
                return (
                  <TableRow
                    key={'edit-ingredient-' + i}
                    item={item}
                    i={i}
                    onRemoveQuantity={onRemoveQuantity}
                    ingredientData={ingredientData}
                    onAddQuantity={onAddQuantity}
                    prices={prices}
                    removeIngredientFromBin={removeIngredientFromBin}
                    euNrv={euNrv}
                    onInputChange={onInputChange}
                    isCustomFormula={isCustomFormula}
                    originalDose={originalDose}
                    showScientificName={showScientificName}
                  />
                );
              })}
          </div>
          <div className="t-w-full t-px-4 t-bg-white t-flex-shrink-0">
            {reviewMessage && <p role="alert" className="t-text-sm t-text-red-600">{reviewMessage}</p>}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                (onReview || handleClose)();
              }}
              disabled={Boolean(onReview && isReviewDisabled)}
              className="disabled:t-opacity-40 t-w-full t-py-3 t-px-4 t-bg-teal-500 hover:t-bg-teal-600 t-text-white t-font-semibold t-rounded-lg t-transition-colors"
            >
              {t(DoneBackToThePage)}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditFormulaModal;
